import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  AUDIENCES,
  audienceLabel,
  getFaqs,
} from "../../store/actions/councilActions/faqAction";
import BreadCrumb from "../BreadCrumb";
import "./faq.css";

/*
  FAQs on the website.

  /faqs                  the general questions
  /faqs/student          the ones written for students
  /faqs/center           for centres, and so on

  The section is in the address, not only in state, so the tile in the Student
  Section on the home page opens straight onto the student questions and can
  be linked to or shared.

  When the questions in a section belong to different trades, a row of trade
  buttons appears above them; when they do not, nothing extra is shown.
*/
const Faqs = () => {
  const dispatch = useDispatch();
  const { audience } = useParams();

  const section = AUDIENCES.some((a) => a.key === audience)
    ? audience
    : "general";

  const [all, setAll] = useState(null);
  const [counts, setCounts] = useState({});
  const [trade, setTrade] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setAll(null);
    setTrade("");
    setOpen(null);
    const { list, counts: totals } = await dispatch(
      getFaqs({ active: 1, audience: section })
    );
    setAll(list);
    setCounts(totals);
  }, [dispatch, section]);

  useEffect(() => {
    load();
  }, [load]);

  // The trades actually represented in this section, so a button is never
  // offered for a trade with no questions behind it.
  const trades = [];
  (all || []).forEach((row) => {
    if (row.trade && !trades.some((t) => String(t.id) === String(row.trade.id))) {
      trades.push(row.trade);
    }
  });

  const rows = (all || []).filter((row) => {
    if (trade === "") return true;
    if (trade === "none") return !row.trade_id;
    return String(row.trade_id) === String(trade);
  });

  const breadcrumbs = [
    { path: "/", label: "Home" },
    { label: `${audienceLabel(section)} FAQs` },
  ];

  return (
    <div class="lists-box">
      <div class="container">
        <BreadCrumb crumbs={breadcrumbs} />

        <div class="title-box">
          <h4>{audienceLabel(section)} FAQs</h4>
        </div>

        {/* the other sections, so a visitor who lands on the wrong one can move */}
        <div class="faq-sections">
          {AUDIENCES.map((a) => (
            <Link
              key={a.key}
              class={`faq-chip ${a.key === section ? "active" : ""}`}
              to={`/faqs/${a.key}`}
            >
              {a.label} <span>({counts[a.key] ?? 0})</span>
            </Link>
          ))}
        </div>

        {trades.length > 0 && (
          <div class="faq-sections mt-2">
            <button
              type="button"
              class={`faq-chip ${trade === "" ? "active" : ""}`}
              onClick={() => setTrade("")}
            >
              All Topics
            </button>
            {trades.map((t) => (
              <button
                type="button"
                key={t.id}
                class={`faq-chip ${String(trade) === String(t.id) ? "active" : ""}`}
                onClick={() => setTrade(t.id)}
              >
                {t.name}
              </button>
            ))}
            <button
              type="button"
              class={`faq-chip ${trade === "none" ? "active" : ""}`}
              onClick={() => setTrade("none")}
            >
              Other
            </button>
          </div>
        )}

        {all === null ? (
          <div class="text-center my-5">
            <Spinner animation="border" />
          </div>
        ) : rows.length === 0 ? (
          <p class="my-5 text-center">
            No questions have been added here yet.
          </p>
        ) : (
          <div class="faq-list my-4">
            {rows.map((row) => (
              <div
                class={`faq-item ${open === row.id ? "open" : ""}`}
                key={row.id}
              >
                <button
                  type="button"
                  class="faq-q"
                  onClick={() => setOpen(open === row.id ? null : row.id)}
                >
                  <span>{row.question}</span>
                  <i class={`fa fa-angle-${open === row.id ? "up" : "down"}`} />
                </button>
                {open === row.id && (
                  <div class="faq-a">
                    {row.trade?.name && (
                      <div class="faq-tag">{row.trade.name}</div>
                    )}
                    {row.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div class="faq-foot">
          <span>Did not find your answer?</span>
          <Link class="faq-contact" to="/contact-us">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Faqs;
